import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { effectivePermissionSetHas } from '@utazzo/shared';
import { toast } from 'sonner';

import * as leadsApi from './leads.api';

import type { LeadStatusChangeRequestListQuery } from '@utazzo/shared';

import { useMyEffectivePermissions } from '@/features/my-profile/use-my-profile';
import { extractServerError } from '@/lib/api-error';
import { useAuthStore } from '@/stores/auth-store';

export const APPROVALS_QK = {
  /**
   * Prefix matching EVERY approvals query - inbox pages and pending
   * counts alike. Approve / reject / cancel / create invalidate through
   * this: a review can now be actioned from the lead detail header
   * (2026-08-01), so the inbox list has to refresh even when it isn't
   * the surface the reviewer clicked on.
   */
  all: () => ['leads', 'status-change-requests'] as const,
  list: (query: LeadStatusChangeRequestListQuery) =>
    ['leads', 'status-change-requests', query] as const,
  // `requestedById` is part of the key so the owner-scoped Daily Tracker tile
  // and the unscoped sidebar badge stay separate cache entries instead of
  // overwriting each other's count.
  pendingCount: (requestedById?: string | null) =>
    ['leads', 'status-change-requests', 'pending-count', requestedById ?? null] as const,
  reviewers: () => ['leads', 'status-change-requests', 'reviewers'] as const,
};

export const useLeadApprovalsList = (query: LeadStatusChangeRequestListQuery) =>
  useQuery({
    queryKey: APPROVALS_QK.list(query),
    queryFn: () => leadsApi.listStatusChangeRequests(query),
  });

/**
 * Options for the "Approved by" filter. Derived server-side from DISTINCT
 * reviewers on the approvals table (reviewers span ADMIN + SALES_LEAD, so the
 * assignable-agents roster used by "Requested by" would miss the admins).
 * Long stale time - the roster of people who have ever reviewed changes rarely.
 */
export const useLeadApprovalReviewers = (enabled = true) =>
  useQuery({
    queryKey: APPROVALS_QK.reviewers(),
    queryFn: () => leadsApi.listStatusChangeRequestReviewers(),
    // Requester-view callers (ADR-0069) never render the "Approved by"
    // filter, so the roster fetch is skipped entirely for them.
    enabled,
    staleTime: 5 * 60_000,
  });

/**
 * Can the signed-in user see the approvals queue at all?
 *
 * 2026-07-22 (ADR-0069). Same predicate the sidebar nav item uses: role
 * ADMIN / SALES_LEAD, or any role an admin granted
 * `ops.lead_approvals.read` from the /roles matrix. Extracted here so
 * every surface that renders a pending-approvals count (sidebar badge,
 * Daily Tracker tile) gates on ONE definition - a role that gains or
 * loses the code can't end up with a tile pointing at a page it can't
 * open, or a dark badge next to a live tile.
 */
export const useCanSeeLeadApprovals = (): boolean => {
  const role = useAuthStore((s) => s.user?.role ?? null);
  const effectivePermissions = useMyEffectivePermissions();
  return (
    role === 'ADMIN' ||
    role === 'SALES_LEAD' ||
    Boolean(
      effectivePermissions &&
      effectivePermissionSetHas(effectivePermissions, 'ops.lead_approvals.read'),
    )
  );
};

/**
 * Pending queue depth. `requestedById` narrows it to one team member's
 * submissions - passed by the Daily Tracker tile so it follows the Lead
 * inbox owner filter. The sidebar badge omits it and keeps the caller-wide
 * count.
 */
export const useLeadApprovalsPendingCount = (enabled = true, requestedById?: string | null) =>
  useQuery({
    queryKey: APPROVALS_QK.pendingCount(requestedById),
    queryFn: () =>
      leadsApi.getPendingStatusChangeRequestCount(requestedById ? { requestedById } : {}),
    enabled,
    refetchInterval: 60_000,
    retry: false,
    throwOnError: false,
  });

/**
 * Admin-only bulk approve, wired to the pending-approvals table's
 * multi-select bar. No bulk reject - rejection needs a per-item reason.
 *
 * Invalidates the same query keys `invalidateLeadCounts` in `use-leads.ts`
 * does (leads list + pipeline stats + this approvals prefix) so the inbox
 * pills, KPI strip and the sidebar pending badge all refresh in lockstep -
 * duplicated inline rather than imported because `use-leads.ts` already
 * imports `APPROVALS_QK` from this file, so importing back would be
 * circular. Approving can flip a lead's stage/status just like a single
 * approve, so the same surfaces need to move.
 *
 * Success is reported by the caller (the bulk-action bar), not here - the
 * response's `approved` / `failed` split is per-call and belongs in one
 * combined toast rather than a generic message from this hook.
 */
export const useBulkApproveLeadApprovals = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => leadsApi.bulkApproveStatusChangeRequests({ ids }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['leads', 'list'] });
      void qc.invalidateQueries({ queryKey: ['leads', 'pipeline-stats'] });
      void qc.invalidateQueries({ queryKey: APPROVALS_QK.all() });
    },
    onError: (err: unknown) =>
      toast.error(extractServerError(err).message ?? 'Failed to approve selected requests'),
  });
};
